import { Icon } from "@spz/ui/components/icon";
import { type VariantProps, cva } from "class-variance-authority";
import type { HTMLAttributes } from "react";
import { cn } from "../lib/utils";

const alertVariants = cva(
  "flex items-center max-w-fit font-title gap-2 relative w-full rounded p-2 text-caption",
  {
    variants: {
      variant: {
        success: "bg-green-50 text-green-700",
        info: "bg-blue-50 text-blue-800",
        critical: "bg-red-50 text-red-900",
        warning: "bg-orange-50 text-orange-900",
      },
    },
    defaultVariants: {
      variant: "info",
    },
  },
);

interface AlertProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  withIcon?: boolean;
}

const Alert = ({
  className,
  variant,
  withIcon = true,
  children,
  ref,
  ...props
}: AlertProps & { ref?: React.Ref<HTMLDivElement> }) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  >
    {withIcon && variant === "success" && (
      <Icon icon="circle-check" size={16} className="text-green-500" />
    )}
    {withIcon && variant === "info" && (
      <Icon icon="info" size={16} className="text-blue-500" />
    )}
    {withIcon && variant === "critical" && (
      <Icon icon="triangle-alert" size={16} className="text-red-500" />
    )}
    {withIcon && variant === "warning" && (
      <Icon icon="triangle-alert" size={16} className="text-orange-500" />
    )}
    {children}
  </div>
);
Alert.displayName = "Alert";

const AlertDescription = ({
  className,
  ref,
  ...props
}: HTMLAttributes<HTMLParagraphElement> & {
  ref?: React.Ref<HTMLDivElement>;
}) => (
  <div
    ref={ref}
    className={cn("[&_p]:leading-relaxed", className)}
    {...props}
  />
);
AlertDescription.displayName = "AlertDescription";

export { Alert, AlertDescription };
